import React, { useState, useEffect } from "react";
import { styled, useTheme, Theme, CSSObject, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQuery } from "@apollo/client";
import DateRangeIcon from "@mui/icons-material/DateRange";
import EventIcon from "@mui/icons-material/Event";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ThemeToggleButton from "../../assets/css/themeToggleButton";
import MenuIcon from "@mui/icons-material/Menu";


import { useIsMobile } from "../../util/utils";
import Logo from "../../assets/images/plany365.ico";
import { useSelectedBusiness } from "../../redux/store";
import { setSelectedBusiness } from "../../redux/actions/businessActions";
import { CREATE_BUSINESS_PROFILE } from "../../util/constants";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { UserContext } from "../../components/auth/auth-context";
import { Permissions, hasPermission } from "../../util/accessControl";
import { GET_BUSINESS_CATEGORIES } from "../../graphql/queries";
import {
  hasCareWorkspaceAccess,
  hasStandaloneBookingAccess,
  isProductSubscribed,
  isProductVisible,
} from "../../../core/business/productAccess";
import {
  isCareCategoryName,
} from "../../../core/business/category";
import {
  detectVerticalFromPath,
  isVerticalId,
} from "../../verticals/registry";
import { getStoredVertical } from "../../routes/verticalPref";
import { getBrandVertical, getNormalizedHostname, isLocalHostname } from "../../util/brandDomains";
import { getRuntimeAppProfile } from "../../runtime/appProfile";

const LOCAL_WORKSPACE_PRODUCTS_KEY = "planyLocalWorkspaceProducts";
const WORKSPACE_PRODUCTS_CHANGED_EVENT = "plany:workspace-products-change";

function readLocalWorkspaceProducts(businessId: number | null) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_WORKSPACE_PRODUCTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const guestProducts = parsed.guest || {};
    const businessProducts = businessId ? parsed[String(businessId)] || {} : {};
    return { ...guestProducts, ...businessProducts };
  } catch {
    return {};
  }
}

function hasLocalWorkspaceOverride(
  localWorkspaceProducts: Record<string, boolean>,
  productId: string
) {
  return Object.prototype.hasOwnProperty.call(localWorkspaceProducts, productId);
}

function resolveWorkspaceProductState(
  providerUser: any,
  localWorkspaceProducts: Record<string, boolean>,
  productId: string,
  defaultWorkspaceAccess = false
) {
  if (hasLocalWorkspaceOverride(localWorkspaceProducts, productId)) {
    return Boolean(localWorkspaceProducts[productId]);
  }

  if (isProductSubscribed(providerUser, productId)) {
    return true;
  }

  return Boolean(defaultWorkspaceAccess);
}

const closedDrawerWidth = 108;
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `${closedDrawerWidth}px`,
  [theme.breakpoints.up("sm")]: {
    width: `${closedDrawerWidth}px`,
  },
});
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5, 1),
  ...theme.mixins.toolbar,
}));
const Drawer = styled(MuiDrawer)(({ theme }) => ({
  width: closedDrawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...closedMixin(theme),
  "& .MuiDrawer-paper": {
    ...closedMixin(theme),
    borderRight: `1px solid ${theme.customChrome?.appBarBorder || alpha("#ffffff", 0.08)}`,
    background: theme.customChrome?.appBarBg || "#22374a",
    boxShadow: "8px 0 22px rgba(12, 25, 39, 0.08)",
    color: theme.customChrome?.brandText || "#edf3f8",
  },
}));
const Content = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  marginTop: 0,
  minWidth: 0,
  overflowX: "auto",
  minHeight: "100vh",
  background:
    theme.customSurfaces?.pageBackgroundGradient || theme.palette.background.default,
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
}));

interface PortalProps {
  TabComponent: React.ComponentType<any>;
  title: string;
  props: any;
}
const Portal: React.FC<PortalProps> = ({ TabComponent, props }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { providerUser } = React.useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const businessId =
    Number(selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId || 0) || null;
  const [localWorkspaceProducts, setLocalWorkspaceProducts] = useState(() =>
    readLocalWorkspaceProducts(businessId)
  );
  useEffect(() => {
    setLocalWorkspaceProducts(readLocalWorkspaceProducts(businessId));
  }, [businessId, location.search]);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleWorkspaceProductsChanged = (event: any) => {
      const detail = event?.detail;
      if (!businessId || Number(detail?.businessId) !== businessId) return;
      setLocalWorkspaceProducts(
        detail?.products && typeof detail.products === "object"
          ? detail.products
          : {}
      );
    };

    window.addEventListener(
      WORKSPACE_PRODUCTS_CHANGED_EVENT,
      handleWorkspaceProductsChanged
    );
    return () => {
      window.removeEventListener(
        WORKSPACE_PRODUCTS_CHANGED_EVENT,
        handleWorkspaceProductsChanged
      );
    };
  }, [businessId]);
  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
  const businessStaffs = React.useMemo(
    () => selectedBusiness?.businessStaffs || [],
    [selectedBusiness?.businessStaffs]
  );
  const isAFHName = React.useCallback(
    (name?: string) => isCareCategoryName(name),
    []
  );
  const selectedCategory = React.useMemo(
    () =>
      selectedBusiness?.businessSubcategory ||
      (categoriesData?.getBusinessCategories || []).find(
        (c: any) =>
          Number(c?.id) ===
          Number(
            selectedBusiness?.businessBasic?.businessSubcategoryId ||
              selectedBusiness?.businessBasic?.businessCategoryId
          )
      ) ||
      null,
    [
      categoriesData?.getBusinessCategories,
      selectedBusiness?.businessBasic?.businessCategoryId,
      selectedBusiness?.businessBasic?.businessSubcategoryId,
      selectedBusiness?.businessSubcategory,
    ]
  );
  const isAFHBusiness = React.useMemo(
    () =>
      isAFHName(selectedCategory?.name) ||
      Boolean(selectedBusiness?.careSettings?.afhLicenseNumber) ||
      Boolean(selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber),
    [
      selectedCategory?.name,
      isAFHName,
      selectedBusiness?.careSettings?.afhLicenseNumber,
      selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber,
    ]
  );
  const isCareEligible = React.useMemo(
    () => isAFHBusiness,
    [isAFHBusiness]
  );
  const canUseStandaloneBooking = React.useMemo(
    () => hasStandaloneBookingAccess(providerUser, isAFHBusiness),
    [providerUser, isAFHBusiness]
  );
  const hasBookingSubscription = React.useMemo(
    () =>
      resolveWorkspaceProductState(
        providerUser,
        localWorkspaceProducts,
        "booking",
        canUseStandaloneBooking
      ),
    [canUseStandaloneBooking, localWorkspaceProducts, providerUser]
  );
  const canUseCareWorkspace = React.useMemo(
    () =>
      resolveWorkspaceProductState(
        providerUser,
        localWorkspaceProducts,
        "care",
        hasCareWorkspaceAccess(providerUser, isCareEligible)
      ),
    [isCareEligible, localWorkspaceProducts, providerUser]
  );
  const activeProductId = getRuntimeAppProfile().verticalId;
  const canUseBookingPortal = React.useMemo(
    () =>
      canUseStandaloneBooking ||
      hasBookingSubscription ||
      isProductVisible(providerUser, "booking"),
    [canUseStandaloneBooking, hasBookingSubscription, providerUser]
  );
  const portalProductId =
    getRuntimeAppProfile().verticalId;
  const isCarePortal = true;
  const isBookingPortal = false;
  const withProduct = React.useCallback(
    (path: string, explicitProductId?: "care" | "booking") =>
      `${path}?v=${explicitProductId || portalProductId}`,
    [portalProductId]
  );
  const homeRoute = isBookingPortal
    ? withProduct("/biz/bookingtypes", "booking")
    : withProduct("/biz/dashboard", "care");
  const currentLoginStaff = React.useMemo(() => {
    const byStaffId = businessStaffs.find(
      (staff: any) => Number(staff?.id) === Number(providerUser?.staffId)
    );
    if (byStaffId) return byStaffId;
    const providerUserId = Number(providerUser?.userId || 0);
    if (!providerUserId) return null;
    return (
      businessStaffs.find(
        (staff: any) => Number(staff?.user?.id) === providerUserId
      ) || null
    );
  }, [businessStaffs, providerUser?.staffId, providerUser?.userId]);
  const rawPermissions = currentLoginStaff?.role?.permissions;
  const userPermissions = React.useMemo(() => {
    if (Array.isArray(rawPermissions)) return rawPermissions.filter(Boolean).map(String);
    if (typeof rawPermissions === "string") {
      try {
        const parsed = JSON.parse(rawPermissions);
        return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
      } catch (_err) {
        return [];
      }
    }
    return [];
  }, [rawPermissions]);
  const can = React.useCallback(
    (permission: string) =>
      hasPermission(
        userPermissions,
        permission,
        currentLoginStaff?.id ?? null,
        currentLoginStaff?.id ?? null
      ),
    [userPermissions, currentLoginStaff?.id]
  );
  const isBusinessOwner =
    Number(selectedBusiness?.businessBasic?.createdBy || 0) ===
    Number(providerUser?.userId || 0);
  const canViewCalendar = isBusinessOwner || can(Permissions.Calendar.VIEW);
  const canViewBookingTypes =
    isBusinessOwner || can(Permissions.BookingType.VIEW);
  const canViewResidents =
    canUseCareWorkspace &&
    isCarePortal &&
    isCareEligible &&
    (
      can(Permissions.Resident.VIEW) ||
      can(Permissions.Business.VIEW) ||
      Number(currentLoginStaff?.id || 0) === Number(providerUser?.staffId || 0)
    );
  const canViewStaff = isBusinessOwner || can(Permissions.Staff.VIEW);
  const canViewJobs =
    canViewBookingTypes ||
    can(Permissions.Job.VIEW) ||
    can(Permissions.Job.ADD) ||
    can(Permissions.Job.EDIT);
  const hasBusiness = Boolean(Number(selectedBusiness?.businessBasic?.id || 0));
  const canViewHiringMarketplace =
    hasBusiness &&
    canUseCareWorkspace &&
    isCarePortal &&
    isCareEligible &&
    canViewJobs;
  const bookingNavLabel = "Booking Types";
  const bookingNavTooltip = "Booking types";
  const calendarNavTooltip = "Booking calendar";
  const subscriptionsPath = `/biz/pricing?product=${portalProductId}&v=${portalProductId}`;
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isActiveRoute = (basePath: string) =>
    location.pathname === basePath ||
    location.pathname.startsWith(`${basePath}/`);
  const [mobileOpen, setMobileOpen] = useState(false);

  const schedulingSectionActive = isActiveRoute("/biz/bookingtypes");
  const careOperationsSectionActive =
    isActiveRoute("/care/schedule") ||
    isActiveRoute("/biz/resident") ||
    isActiveRoute("/biz/staff");
  const isHiringActive =
    isActiveRoute("/biz/hiring") ||
    isActiveRoute("/biz/manage/jobs") ||
    /^\/b\/[^/]+\/manage\/jobs(\/|$)/.test(location.pathname);
  const isMarketplaceActive = isActiveRoute("/biz/marketplace");
  const growthSectionActive = isHiringActive || isMarketplaceActive;
  const settingsSectionActive =
    isActiveRoute("/biz/business") ||
    isActiveRoute("/biz/pricing") ||
    isActiveRoute("/hipaa-security");
  const [schedulingOpen, setSchedulingOpen] = useState(schedulingSectionActive);
  const [careOperationsOpen, setCareOperationsOpen] = useState(
    careOperationsSectionActive
  );
  const [growthOpen, setGrowthOpen] = useState(growthSectionActive);
  const [settingsOpen, setSettingsOpen] = useState(settingsSectionActive);
  const collapsedDesktop = !isMobile;

  useEffect(() => {
    if (schedulingSectionActive) setSchedulingOpen(true);
  }, [schedulingSectionActive]);

  useEffect(() => {
    if (careOperationsSectionActive) setCareOperationsOpen(true);
  }, [careOperationsSectionActive]);

  useEffect(() => {
    if (growthSectionActive) setGrowthOpen(true);
  }, [growthSectionActive]);

  useEffect(() => {
    if (settingsSectionActive) setSettingsOpen(true);
  }, [settingsSectionActive]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const rootStyle = document.documentElement.style;

    if (isMobile) {
      rootStyle.setProperty(
        "--portal-drawer-offset",
        "calc(64px + 1px)"
      );
      rootStyle.setProperty("--portal-mobile-toggle-space", "56px");
    } else {
      const collapsedOffset = `${closedDrawerWidth}px`;
      rootStyle.setProperty(
        "--portal-drawer-offset",
        collapsedOffset
      );
      rootStyle.setProperty("--portal-mobile-toggle-space", "0px");
    }

    return () => {
      rootStyle.removeProperty("--portal-drawer-offset");
      rootStyle.removeProperty("--portal-mobile-toggle-space");
    };
  }, [isMobile]);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const isPricing = location.pathname === "/biz/pricing";
    const isBillingFlow = location.pathname.startsWith("/billing/");
    if (!isPricing && !isBillingFlow) {
      try {
        localStorage.setItem("lastBizRouteBeforePricing", currentPath);
      } catch (_error) {
        // ignore storage failures
      }
    }
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const raw = localStorage.getItem("pendingUpgradeNavigation");
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const path = typeof parsed?.path === "string" ? parsed.path : "";
      const targetBusinessId = Number(parsed?.businessId || 0);
      const createdAt = Number(parsed?.createdAt || 0);
      const isFresh = createdAt > 0 && Date.now() - createdAt < 2 * 60 * 60 * 1000;

      if (!path || !isFresh) {
        localStorage.removeItem("pendingUpgradeNavigation");
        return;
      }
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      if (currentPath === path) {
        localStorage.removeItem("pendingUpgradeNavigation");
        return;
      }

      if (
        targetBusinessId > 0 &&
        Number(selectedBusiness?.businessBasic?.id || 0) !== targetBusinessId
      ) {
        dispatch(setSelectedBusiness(targetBusinessId));
      }

      localStorage.removeItem("pendingUpgradeNavigation");
      navigate(path, { replace: true });
    } catch (_error) {
      localStorage.removeItem("pendingUpgradeNavigation");
    }
  }, [location.pathname, location.search, location.hash, navigate, dispatch, selectedBusiness]);

  const handleMobileToggle = () => setMobileOpen((o) => !o);
  const handleNavigation = (
    path: string,
    event: React.MouseEvent,
    mobile = false
  ) => {
    navigate(path, { state: { from: window.location.href } });
    if (mobile) setMobileOpen(false);
  };
  const listItemTextStyle = {
    ...(collapsedDesktop
      ? {
        mt: 0.55,
        mb: 0,
        textAlign: "center",
        "& .MuiTypography-root": {
          fontSize: "0.7rem",
          lineHeight: 1.2,
          whiteSpace: "normal",
          wordBreak: "normal",
          letterSpacing: "0.015em",
        },
      }
      : {
        "& .MuiTypography-root": {
          fontSize: "0.9rem",
          letterSpacing: "0.01em",
        },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }),
  };

  const listLayoutSx = {
    flexShrink: 0,
    "& .MuiListItemButton-root": {
      mx: collapsedDesktop ? 1 : 1.25,
      my: collapsedDesktop ? 0.8 : 0.55,
      px: collapsedDesktop ? 1 : 1.5,
      py: collapsedDesktop ? 1.25 : 0.95,
      borderRadius: 3.25,
      minHeight: collapsedDesktop ? 76 : 52,
      transition: "all 180ms ease",
      color: alpha("#edf3f8", 0.78),
      "&:hover": {
        bgcolor: alpha("#ffffff", 0.07),
        color: "#ffffff",
        transform: "translateY(-1px)",
      },
    },
    "& .MuiListItem-root": collapsedDesktop
      ? {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }
      : {},
    "& .MuiListItemIcon-root": {
      minWidth: collapsedDesktop ? 0 : 36,
      justifyContent: "center",
      mb: collapsedDesktop ? 0.65 : 0,
      color: "inherit",
      opacity: 0.95,
      "& .MuiSvgIcon-root": {
        fontSize: collapsedDesktop ? 22 : 21,
      },
    },
    "& .MuiTypography-root": {
      color: "inherit",
      fontWeight: 500,
    },
  };

  const submenuListSx = {
    mx: collapsedDesktop ? 1.25 : 2,
    mb: 0.75,
    py: 0.5,
    pl: collapsedDesktop ? 0.75 : 1,
    borderLeft: `2px solid ${alpha("#ffffff", 0.22)}`,
    borderRadius: 2,
    bgcolor: alpha("#071828", 0.16),
  };

  const submenuItemSx = {
    mx: "0 !important",
    my: "2px !important",
    px: collapsedDesktop ? "6px !important" : "10px !important",
    py: "7px !important",
    minHeight: `${collapsedDesktop ? 48 : 42}px !important`,
    borderRadius: "8px !important",
    alignItems: "center",
    ...(collapsedDesktop
      ? {
        flexDirection: "column",
        gap: 0.25,
      }
      : {
        flexDirection: "row",
      }),
    "& .MuiListItemIcon-root": {
      minWidth: collapsedDesktop ? 0 : 28,
      mb: collapsedDesktop ? 0.25 : 0,
      opacity: 0.8,
      "& .MuiSvgIcon-root": {
        fontSize: 17,
      },
    },
  };

  const submenuTextStyle = {
    width: "100%",
    minWidth: 0,
    m: 0,
    textAlign: collapsedDesktop ? "center" : "left",
    "& .MuiTypography-root": {
      display: "block",
      width: "100%",
      fontSize: collapsedDesktop ? "0.64rem" : "0.82rem",
      lineHeight: 1.15,
      whiteSpace: "normal",
      overflowWrap: "break-word",
      wordBreak: "normal",
      letterSpacing: "0.01em",
    },
  };

  const mainListItems = (
    <List
      sx={{
        ...listLayoutSx,
        "& .Mui-selected": {
          bgcolor: alpha("#ffffff", 0.1),
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
        },
        "& .Mui-selected .MuiListItemIcon-root": {
          color: "#ffffff",
        },
        "& .Mui-selected .MuiTypography-root": {
          color: "#ffffff",
          fontWeight: 700,
        },
      }}
    >
      {/* AFH Dashboard */}
      {canUseCareWorkspace && isCarePortal && (
        <Tooltip
          title={
            hasBusiness
              ? isCareEligible
                ? "Care Provider Dashboard"
                : "Complete Care setup"
              : CREATE_BUSINESS_PROFILE
          }
          placement="right"
        >
          <span>
            <ListItem
              button
              selected={isActiveRoute("/biz/dashboard")}
              disabled={!hasBusiness}
              onClick={(e) => handleNavigation(withProduct("/biz/dashboard", "care"), e)}
            >
              <ListItemIcon>
                <SpaceDashboardRoundedIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" sx={listItemTextStyle} />
            </ListItem>
          </span>
        </Tooltip>
      )}
      {/* Plany Care operations */}
      {canUseCareWorkspace && isCarePortal && (
        <>
          <Tooltip title="Operations" placement="right">
            <ListItem
              button
              selected={careOperationsSectionActive}
              aria-expanded={careOperationsOpen}
              onClick={() => setCareOperationsOpen((open) => !open)}
            >
              <ListItemIcon><AssignmentIndIcon /></ListItemIcon>
              <ListItemText primary="Operations" sx={listItemTextStyle} />
              {!collapsedDesktop &&
                (careOperationsOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />)}
            </ListItem>
          </Tooltip>
          <Collapse in={careOperationsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={submenuListSx}>
              <Tooltip
                title={hasBusiness ? "Configure assessments, tours, and intake scheduling" : CREATE_BUSINESS_PROFILE}
                placement="right"
              >
                <span>
                  <ListItem
                    button
                    selected={isActiveRoute("/care/schedule")}
                    disabled={!hasBusiness}
                    onClick={(e) => handleNavigation(withProduct("/care/schedule", "care"), e)}
                    sx={submenuItemSx}
                  >
                    <ListItemIcon><EventIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                    <ListItemText primary="Scheduling" sx={submenuTextStyle} />
                  </ListItem>
                </span>
              </Tooltip>
              {isCareEligible && (
                <Tooltip
                  title={hasBusiness ? "Residents" : CREATE_BUSINESS_PROFILE}
                  placement="right"
                >
                  <span>
                    <ListItem
                      button
                      selected={isActiveRoute("/biz/resident")}
                      disabled={!hasBusiness || !canViewResidents}
                      onClick={(e) => handleNavigation(withProduct("/biz/resident", "care"), e)}
                      sx={submenuItemSx}
                    >
                      <ListItemIcon><AssignmentIndIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                      <ListItemText primary="Residents" sx={submenuTextStyle} />
                    </ListItem>
                  </span>
                </Tooltip>
              )}
              <Tooltip
                title={hasBusiness ? "Staff" : CREATE_BUSINESS_PROFILE}
                placement="right"
              >
                <span>
                  <ListItem
                    button
                    selected={isActiveRoute("/biz/staff")}
                    disabled={!hasBusiness || !canViewStaff}
                    onClick={(e) => handleNavigation(withProduct("/biz/staff", "care"), e)}
                    sx={submenuItemSx}
                  >
                    <ListItemIcon><PeopleIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                    <ListItemText primary="Staff" sx={submenuTextStyle} />
                  </ListItem>
                </span>
              </Tooltip>
            </List>
          </Collapse>
        </>
      )}
      {/* Timely365 scheduling tools */}
      {isBookingPortal && canUseBookingPortal && (
        <>
          <Tooltip title="Scheduling" placement="right">
            <ListItem
              button
              selected={schedulingSectionActive}
              aria-expanded={schedulingOpen}
              onClick={() => setSchedulingOpen((open) => !open)}
            >
              <ListItemIcon>
                <EventIcon />
              </ListItemIcon>
              <ListItemText primary="Scheduling" sx={listItemTextStyle} />
              {!collapsedDesktop &&
                (schedulingOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />)}
            </ListItem>
          </Tooltip>
          <Collapse in={schedulingOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={submenuListSx}>
              <Tooltip
                title={hasBusiness ? bookingNavTooltip : CREATE_BUSINESS_PROFILE}
                placement="right"
              >
                <span>
                  <ListItem
                    button
                    selected={isActiveRoute("/biz/bookingtypes")}
                    disabled={!hasBusiness || !canViewBookingTypes}
                    onClick={(e) => handleNavigation(withProduct("/biz/bookingtypes", "booking"), e)}
                    sx={submenuItemSx}
                  >
                    <ListItemIcon><EventIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                    <ListItemText primary={bookingNavLabel} sx={submenuTextStyle} />
                  </ListItem>
                </span>
              </Tooltip>
            </List>
          </Collapse>
        </>
      )}
      {/* Calendar */}
      {isBookingPortal && canUseBookingPortal && (
        <Tooltip
          title={hasBusiness ? calendarNavTooltip : CREATE_BUSINESS_PROFILE}
          placement="right"
        >
          <span>
            <ListItem
              button
              selected={isActiveRoute("/biz/calendar")}
              disabled={!hasBusiness || !canViewCalendar}
              onClick={(e) => handleNavigation(withProduct("/biz/calendar", "booking"), e)}
            >
              <ListItemIcon>
                <DateRangeIcon />
              </ListItemIcon>
              <ListItemText primary="Calendar" sx={listItemTextStyle} />
            </ListItem>
          </span>
        </Tooltip>
      )}
      {/* Timely365 team */}
      {isBookingPortal && (
        <Tooltip
          title={hasBusiness ? "Team member" : CREATE_BUSINESS_PROFILE}
          placement="right"
        >
          <span>
            <ListItem
              button
              selected={isActiveRoute("/biz/staff")}
              disabled={!hasBusiness || !canViewStaff}
              onClick={(e) => handleNavigation(withProduct("/biz/staff"), e)}
            >
              <ListItemIcon><PeopleIcon /></ListItemIcon>
              <ListItemText primary="Team" sx={listItemTextStyle} />
            </ListItem>
          </span>
        </Tooltip>
      )}
    </List>
  );

  const bottomListItems = (
    <List
      sx={{
        ...listLayoutSx,
        mt: "auto",
        "& .Mui-selected": {
          bgcolor: alpha("#ffffff", 0.1),
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
        },
        "& .Mui-selected .MuiListItemIcon-root": {
          color: "#ffffff",
        },
        "& .Mui-selected .MuiTypography-root": {
          color: "#ffffff",
          fontWeight: 700,
        },
      }}
    >

      <Divider />
      {canViewHiringMarketplace && (
        <>
          <Tooltip title="Growth" placement="right">
            <ListItem
              button
              selected={growthSectionActive}
              aria-expanded={growthOpen}
              onClick={() => setGrowthOpen((open) => !open)}
            >
              <ListItemIcon><WorkOutlineRoundedIcon /></ListItemIcon>
              <ListItemText primary="Growth" sx={listItemTextStyle} />
              {!collapsedDesktop &&
                (growthOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />)}
            </ListItem>
          </Tooltip>
          <Collapse in={growthOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={submenuListSx}>
              <ListItem
                button
                selected={isHiringActive}
                onClick={(e) => handleNavigation(withProduct("/biz/manage/jobs", "care"), e)}
                sx={submenuItemSx}
              >
                <ListItemIcon><WorkOutlineRoundedIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                <ListItemText primary="Hiring" sx={submenuTextStyle} />
              </ListItem>
              <ListItem
                button
                selected={isMarketplaceActive}
                onClick={(e) => handleNavigation(withProduct("/biz/marketplace", "care"), e)}
                sx={submenuItemSx}
              >
                <ListItemIcon><StorefrontRoundedIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                <ListItemText primary="Marketplace" sx={submenuTextStyle} />
              </ListItem>
            </List>
          </Collapse>
        </>
      )}
      <Divider />
      {isBookingPortal ? (
        <>
          <Tooltip title="Settings" placement="right">
            <ListItem
              button
              selected={settingsSectionActive}
              aria-expanded={settingsOpen}
              onClick={() => setSettingsOpen((open) => !open)}
            >
              <ListItemIcon><SettingsRoundedIcon /></ListItemIcon>
              <ListItemText primary="Settings" sx={listItemTextStyle} />
              {!collapsedDesktop &&
                (settingsOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />)}
            </ListItem>
          </Tooltip>
          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={submenuListSx}>
              <ListItem
                button
                selected={isActiveRoute("/biz/business")}
                onClick={(e) => handleNavigation(withProduct("/biz/business"), e, isMobile)}
                sx={submenuItemSx}
              >
                <Tooltip title="Business Profile" placement="right">
                  <ListItemIcon><BusinessIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Business Profile" sx={submenuTextStyle} />
              </ListItem>
              <ListItem
                button
                selected={isActiveRoute("/biz/pricing")}
                onClick={(e) => handleNavigation(subscriptionsPath, e, isMobile)}
                sx={submenuItemSx}
              >
                <Tooltip title="Subscription & Billing" placement="right">
                  <ListItemIcon><UpgradeIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Billing" sx={submenuTextStyle} />
              </ListItem>
              <ListItem
                button
                selected={isActiveRoute("/hipaa-security")}
                onClick={(e) => handleNavigation("/hipaa-security", e, isMobile)}
                sx={submenuItemSx}
              >
                <Tooltip title="HIPAA & Security" placement="right">
                  <ListItemIcon><SecurityIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Security" sx={submenuTextStyle} />
              </ListItem>
              <ListItem button sx={submenuItemSx}>
                <Tooltip title="Appearance" placement="right">
                  <ListItemIcon><ThemeToggleButton /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Appearance" sx={submenuTextStyle} />
              </ListItem>
            </List>
          </Collapse>
        </>
      ) : (
        <>
          <Tooltip title="Settings" placement="right">
            <ListItem
              button
              selected={settingsSectionActive}
              aria-expanded={settingsOpen}
              onClick={() => setSettingsOpen((open) => !open)}
            >
              <ListItemIcon><SettingsRoundedIcon /></ListItemIcon>
              <ListItemText primary="Settings" sx={listItemTextStyle} />
              {!collapsedDesktop &&
                (settingsOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />)}
            </ListItem>
          </Tooltip>
          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={submenuListSx}>
              <ListItem
                button
                selected={isActiveRoute("/biz/business")}
                onClick={(e) => handleNavigation(withProduct("/biz/business"), e, isMobile)}
                sx={submenuItemSx}
              >
                <ListItemIcon><BusinessIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                <ListItemText primary="Business Profile" sx={submenuTextStyle} />
              </ListItem>
              <ListItem
                button
                selected={isActiveRoute("/biz/pricing")}
                onClick={(e) => handleNavigation(subscriptionsPath, e, isMobile)}
                sx={submenuItemSx}
              >
                <ListItemIcon><UpgradeIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                <ListItemText primary="Billing" sx={submenuTextStyle} />
              </ListItem>
              <ListItem
                button
                selected={isActiveRoute("/hipaa-security")}
                onClick={(e) => handleNavigation("/hipaa-security", e, isMobile)}
                sx={submenuItemSx}
              >
                <ListItemIcon><SecurityIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                <ListItemText primary="HIPAA & Security" sx={submenuTextStyle} />
              </ListItem>
              <ListItem button sx={submenuItemSx}>
                <ListItemIcon><ThemeToggleButton /></ListItemIcon>
                <ListItemText primary="Appearance" sx={submenuTextStyle} />
              </ListItem>
            </List>
          </Collapse>
        </>
      )}
    </List>
  );

  const drawer = (
    <>
      <DrawerHeader>
        <Box
          component={RouterLink}
          to={hasBusiness ? homeRoute : "/biz/business"}
          sx={{
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "none",
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.06)",
            boxShadow: "0 10px 22px rgba(0,0,0,0.14)",
          }}
        >
          <img
            src={Logo}
            alt="Logo"
            style={{
              width: 28,
              height: 28,
              objectFit: "contain",
            }}
          />
        </Box>
      </DrawerHeader>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pt: 0.75,
          pb: 1.25,
        }}
      >
        {mainListItems}
        {bottomListItems}
      </Box>
    </>
  );

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleMobileToggle}
            sx={{
              position: "fixed",
              top:
                location.pathname === "/profile"
                  ? theme.spacing(2.5)
                  : theme.spacing(1),
              left: theme.spacing(1),
              zIndex: theme.zIndex.drawer + 2,
              color: "#f8fbff",
              border: "1px solid rgba(255,255,255,0.16)",
              bgcolor: "rgba(10,26,42,0.76)",
              backdropFilter: "blur(18px)",
              boxShadow: "0 18px 30px rgba(10, 28, 44, 0.18)",
              "&:hover": {
                bgcolor: "rgba(13,35,55,0.9)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {!isMobile ? (
          <Drawer variant="permanent">
            {drawer}
          </Drawer>
        ) : (
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleMobileToggle}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: 288,
                background: "#22374a",
                color: "#edf3f8",
              },
            }}
          >
            {drawer}
          </MuiDrawer>
        )}
        <Content>
          <TabComponent {...props} />
        </Content>

      </Box>
    </>
  );
};

export default Portal;
